/**
 * <kit-brand-mark> — the platform brand lockup as a web-kit element.
 *
 * The mark is the platform P reduced to two detached strokes and a full
 * stop (see `geometry.ts`); the APP NAME sits in the measurable gap between
 * stem and bowl, on the shared baseline; the full lockup is a breadcrumb:
 *
 *   {P + AppName.} | {Surface} › {Context} › …
 *
 * Attributes (identity, observed):
 *   app        — the app name in the slot (»DNA«, »Career«, »Bewerbungen«)
 *   space      — one of the five platform spaces; colors the dot
 *   surface    — first breadcrumb crumb (»Suche«, »Workshop«, …)
 *   breadcrumb — context crumbs, »›«- or »>«-separated string
 *                (the `breadcrumb` PROPERTY accepts a string array)
 *   animated   — plays the `enter` timeline on connect
 *
 * Animation API (typed, promise-returning — see `animation.ts`):
 *   el.play("enter")
 *   el.play("app-switch", { to: "Career" })
 *   el.play("dot-bounce")
 *   el.set({ app, space, surface, breadcrumb })
 *
 * The dot's x-position and the viewBox width are ELASTIC: the element
 * measures the rendered app name (`getComputedTextLength`, falling back to
 * an estimate off-browser) and re-renders once when the measurement differs
 * — including after web fonts load.
 */

import { signal, type ReadonlySignal, type Signal } from "../core/signal";
import { KitElement } from "../core/element";
import { flushed } from "../core/scheduler";
import type { ViewModel } from "../core/viewmodel";
import { defineElement } from "../core/define";
import { html, svg, nothing, repeat, type TemplateResult } from "../render/html";
import {
  STEM_PATH,
  BOWL_PATH,
  DOT,
  SLOT,
  VIEW,
  ANCHORS,
  dotCx,
  viewWidth,
  estimateNameWidth,
} from "./geometry";
import { isSpace, type Space } from "./spaces";
import { installBrandStyles } from "./styles";
import {
  cancelAll,
  playDotBounce,
  playEnter,
  playLettersIn,
  playLettersOut,
  type BrandAnimationName,
  type BrandParts,
} from "./animation";

export interface BrandState {
  app?: string | null;
  space?: Space | null;
  surface?: string | null;
  breadcrumb?: readonly string[];
}

export interface PlayOptions {
  /** `app-switch` only: the app name to switch to. */
  to?: string;
}

class BrandMarkVm implements ViewModel {
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>;

  constructor(
    readonly app: Signal<string>,
    readonly space: Signal<Space | null>,
    readonly surface: Signal<string>,
    readonly breadcrumb: Signal<readonly string[]>,
    readonly nameWidth: Signal<number>
  ) {
    this.renderDeps = [app, space, surface, breadcrumb, nameWidth];
  }

  /** All crumbs after the mark: surface first, then the context trail. */
  get crumbs(): readonly string[] {
    const head = this.surface.value.trim();
    return head.length > 0
      ? [head, ...this.breadcrumb.value]
      : [...this.breadcrumb.value];
  }
}

const parseCrumbs = (raw: string | null): readonly string[] =>
  (raw ?? "")
    .split(/[>›]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

export class KitBrandMark extends KitElement<BrandMarkVm> {
  static override readonly observedAttributes: readonly string[] = [
    ...KitElement.observedAttributes,
    "app",
    "space",
    "surface",
    "breadcrumb",
    "animated",
  ];

  // Signals live on the element so properties can be set BEFORE connect.
  readonly #app = signal("");
  readonly #space = signal<Space | null>(null);
  readonly #surface = signal("");
  readonly #breadcrumb = signal<readonly string[]>([]);
  readonly #nameWidth = signal(0);
  readonly #animations: Animation[] = [];
  #entered = false;

  get app(): string {
    return this.#app.value;
  }
  set app(name: string) {
    if (name === this.#app.value) return;
    this.#app.set(name);
    this.#nameWidth.set(estimateNameWidth(name));
    if (this.getAttribute("app") !== name) this.setAttribute("app", name);
  }

  get space(): Space | null {
    return this.#space.value;
  }
  set space(value: Space | null) {
    this.#space.set(value);
    if (value === null) this.removeAttribute("space");
    else if (this.getAttribute("space") !== value)
      this.setAttribute("space", value);
  }

  get surface(): string {
    return this.#surface.value;
  }
  set surface(value: string) {
    this.#surface.set(value);
    if (this.getAttribute("surface") !== value)
      this.setAttribute("surface", value);
  }

  get breadcrumb(): readonly string[] {
    return this.#breadcrumb.value;
  }
  set breadcrumb(crumbs: readonly string[]) {
    this.#breadcrumb.set([...crumbs]);
  }

  override attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (oldValue === newValue) return;
    switch (name) {
      case "app": {
        const app = newValue ?? "";
        if (app !== this.#app.value) {
          this.#app.set(app);
          this.#nameWidth.set(estimateNameWidth(app));
        }
        break;
      }
      case "space":
        this.#space.set(isSpace(newValue) ? newValue : null);
        break;
      case "surface":
        this.#surface.set(newValue ?? "");
        break;
      case "breadcrumb":
        this.#breadcrumb.set(parseCrumbs(newValue));
        break;
      default:
        break;
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Re-measure once web fonts arrive — Fraunces changes the name's width.
    const fonts = (document as { fonts?: { ready?: Promise<unknown> } }).fonts;
    void fonts?.ready?.then(() => {
      if (this.isConnected) this.#measure();
    });
    if (this.hasAttribute("animated") && !this.#entered) {
      this.#entered = true;
      void flushed().then(() => this.play("enter"));
    }
  }

  override disconnectedCallback(): void {
    cancelAll(this.#animations);
    super.disconnectedCallback();
  }

  protected createViewModel(): BrandMarkVm {
    return new BrandMarkVm(
      this.#app,
      this.#space,
      this.#surface,
      this.#breadcrumb,
      this.#nameWidth
    );
  }

  /** Set several identity facets at once (sync; no animation). */
  set(state: BrandState): void {
    if (state.app !== undefined) this.app = state.app ?? "";
    if (state.space !== undefined) this.space = state.space;
    if (state.surface !== undefined) this.surface = state.surface ?? "";
    if (state.breadcrumb !== undefined) this.breadcrumb = state.breadcrumb;
  }

  /** Play a named timeline. Always resolves; reduced motion short-circuits. */
  async play(name: BrandAnimationName, options: PlayOptions = {}): Promise<void> {
    cancelAll(this.#animations);
    switch (name) {
      case "enter":
        return playEnter(this.#parts(), this.#animations);
      case "dot-bounce":
        return playDotBounce(this.#parts(), this.#animations);
      case "app-switch": {
        const to = options.to ?? "";
        if (to === this.#app.value) return;
        const fromCx = dotCx(this.#nameWidth.value);
        await playLettersOut(this.#parts(), this.#animations);
        this.app = to;
        await flushed();
        this.#measure();
        await flushed();
        // Drop the out-phase `fill: both` holds — lit reuses tspans whose
        // repeat keys survive the switch, and a lingering hold would keep
        // them invisible after the in-phase animation ends.
        cancelAll(this.#animations);
        const travel = dotCx(this.#nameWidth.value) - fromCx;
        return playLettersIn(this.#parts(), travel, this.#animations);
      }
    }
  }

  /** Scheduler flush + one measurement pass (elastic dot/viewBox). */
  override flushRender(): void {
    super.flushRender();
    this.#measure();
  }

  #measure(): void {
    const name = this.#app.value;
    if (name.length === 0) {
      if (this.#nameWidth.value !== 0) this.#nameWidth.set(0);
      return;
    }
    const text = this.querySelector(".app-name") as
      | (SVGTextElement & { getComputedTextLength?: () => number })
      | null;
    if (!text || typeof text.getComputedTextLength !== "function") return;
    try {
      const width = text.getComputedTextLength();
      if (width > 0 && Math.abs(width - this.#nameWidth.value) > 0.5) {
        this.#nameWidth.set(width); // renderDeps → one corrective re-render
      }
    } catch {
      /* not laid out yet — the estimate stands */
    }
  }

  #parts(): BrandParts {
    return {
      host: this,
      stem: this.querySelector(".p-stem"),
      bowl: this.querySelector(".p-bowl"),
      dot: this.querySelector(".p-dot"),
      letters: [...this.querySelectorAll(".app-letter")],
    };
  }

  protected render(vm: BrandMarkVm): TemplateResult {
    const app = vm.app.value;
    const width = vm.nameWidth.value;
    const cx = dotCx(width);
    const vw = viewWidth(width);
    const { crumbs } = vm;
    const letters = [...app].map((ch) => (ch === " " ? " " : ch));
    return html`<svg
        class="brand-mark"
        viewBox="0 0 ${vw} ${VIEW.height}"
        role="img"
        aria-label=${app.length > 0 ? `${app}.` : "P."}
      >
        <path class="p-stem" d=${STEM_PATH}></path>
        <path class="p-bowl" d=${BOWL_PATH}></path>
        <text class="app-name" x=${SLOT.x} y=${SLOT.baseline} aria-hidden="true"
          >${repeat(
            letters,
            (ch, i) => `${i}:${ch}`,
            (ch, i) =>
              svg`<tspan class="app-letter" style="--i:${i}">${ch}</tspan>`
          )}</text
        >
        <circle class="p-dot" cx=${cx} cy=${DOT.cy} r=${DOT.r}></circle>
        <g class="anchors" aria-hidden="true">
          <circle class="anchor-baseline-start" cx=${ANCHORS.baselineStart.x} cy=${ANCHORS.baselineStart.y} r="0"></circle>
          <circle class="anchor-slot-start" cx=${ANCHORS.slotStart.x} cy=${ANCHORS.slotStart.y} r="0"></circle>
          <circle class="anchor-bowl-hinge" cx=${ANCHORS.bowlHinge.x} cy=${ANCHORS.bowlHinge.y} r="0"></circle>
          <circle class="anchor-bowl-tip" cx=${ANCHORS.bowlTip.x} cy=${ANCHORS.bowlTip.y} r="0"></circle>
          <circle class="anchor-dot" cx=${cx} cy=${DOT.cy} r="0"></circle>
        </g>
      </svg>
      ${crumbs.length > 0
        ? html`<span class="divider" aria-hidden="true"></span>
            <nav class="breadcrumb" aria-label="Pfad">
              ${repeat(
                crumbs,
                (crumb, i) => `${i}:${crumb}`,
                (crumb, i) =>
                  html`${i > 0
                      ? html`<span class="crumb-sep" aria-hidden="true">›</span>`
                      : nothing}<span class="crumb" title=${crumb}>${crumb}</span>`
              )}
            </nav>`
        : nothing}`;
  }
}

/** Register `<kit-brand-mark>` + install the brand stylesheet (idempotent). */
export function defineBrandMark(): void {
  installBrandStyles();
  defineElement("kit-brand-mark", KitBrandMark);
}
